import { useMemo, useState } from 'react';
import { Eye, EyeOff, Inbox, Link2, Search, Tag } from 'lucide-react';
import clsx from 'clsx';
import { useAllMailboxes, useBrands, useSuperOverview } from '@/lib/queries';
import { formatCompact, formatNumber, formatPercent, formatRelative } from '@/lib/format';
import { Pagination } from '@/components/Pagination';
import { StatCard } from '@/components/StatCard';
import { InfoButton } from '@/components/InfoButton';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Input,
  TableSkeleton,
  humanizeStatus,
  statusTone,
} from '@/components/ui';

/**
 * Platform-wide view of every mailbox and every brand seen across all users.
 *
 * Credentials are only fetched when revealed — a distinct request the server
 * audits — rather than being sent with the page and hidden in the markup.
 */
export function SuperMailsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [reveal, setReveal] = useState(false);

  const overview = useSuperOverview();
  const brands = useBrands(25);

  const params = useMemo(
    () => ({ page, pageSize: 50, search: search || undefined, revealSecrets: reveal }),
    [page, search, reveal],
  );
  const { data, isLoading, isPlaceholderData } = useAllMailboxes(params);

  const totals = overview.data?.totals;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-slate-100">Mail &amp; brands</h1>
        <p className="mt-1 text-sm text-slate-500">
          Every mailbox and every sender across the platform, regardless of user.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Mailboxes"
          value={formatNumber(overview.data?.mailboxes.total ?? 0)}
          sublabel={
            overview.data
              ? `${overview.data.mailboxes.healthy} healthy · ${overview.data.mailboxes.failing} failing`
              : undefined
          }
          icon={<Inbox className="h-4 w-4" />}
          tone={overview.data && overview.data.mailboxes.failing > 0 ? 'warning' : 'brand'}
          info="Every address connected by every user. Failing usually means a rejected app password."
          loading={overview.isLoading}
        />
        <StatCard
          label="Mails matched"
          value={formatCompact(totals?.messagesMatched ?? 0)}
          sublabel="all users, all time"
          info="Messages matching a user's keywords. Mail that matched nothing is counted for the daily totals and then discarded, never stored."
          loading={overview.isLoading}
        />
        <StatCard
          label="Mails opened"
          value={formatCompact(totals?.mailsOpened ?? 0)}
          sublabel={totals ? `${formatNumber(totals.linksFound)} found` : undefined}
          icon={<Link2 className="h-4 w-4" />}
          tone="success"
          info="Matched mail with at least one link that was reached, counted once per mail however many links it held."
          loading={overview.isLoading}
        />
        <StatCard
          label="Not opened"
          value={formatCompact(totals?.mailsNotOpened ?? 0)}
          sublabel={
            totals && totals.mailsOpened + totals.mailsNotOpened > 0
              ? `${formatPercent((totals.mailsOpened / (totals.mailsOpened + totals.mailsNotOpened)) * 100)} success rate`
              : undefined
          }
          tone={totals && totals.mailsNotOpened > 0 ? 'danger' : 'default'}
          info="Matched mail that had links, none of which could be reached."
          loading={overview.isLoading}
        />
      </div>

      <Card>
        <CardHeader
          title="Brands"
          description="Most frequent senders across every monitored mailbox"
          action={<Tag className="h-4 w-4 text-slate-600" aria-hidden />}
        />
        {brands.isLoading ? (
          <TableSkeleton rows={6} columns={5} />
        ) : brands.data?.length ? (
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead>
                <tr>
                  <th>
                    <span className="inline-flex items-center gap-1.5">
                      Brand
                      <InfoButton label="Brand">
                        The sender, normalised from its domain. Sending subdomains collapse together, so
                        news.amazon.in and email.amazon.in both count as one brand.
                      </InfoButton>
                    </span>
                  </th>
                  <th className="text-right">Mails</th>
                  <th className="text-right">Links</th>
                  <th className="text-right">Opened</th>
                  <th className="text-right">
                    <span className="inline-flex items-center justify-end gap-1.5">
                      Users reached
                      <InfoButton label="Users reached" align="right">
                        How many distinct users received mail from this brand. Ten messages to one user count as one.
                      </InfoButton>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {brands.data.map((brand) => (
                  <tr key={brand.brand}>
                    <td>
                      <Badge tone="brand">{brand.brand}</Badge>
                    </td>
                    <td className="text-right tabular-nums text-slate-200">{formatNumber(brand.messages)}</td>
                    <td className="text-right tabular-nums text-slate-300">{formatNumber(brand.links)}</td>
                    <td className="text-right tabular-nums text-emerald-400">{formatNumber(brand.visitsSuccess)}</td>
                    <td className="text-right tabular-nums text-slate-300">{formatNumber(brand.reach)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<Tag className="h-6 w-6" />}
            title="No brands yet"
            description="Brands appear once mail matching users' keywords starts arriving."
          />
        )}
      </Card>

      <Card>
        <CardHeader
          title="All mailboxes"
          description="Every address added by every user"
          action={
            <Button
              size="sm"
              variant={reveal ? 'secondary' : 'ghost'}
              onClick={() => setReveal((v) => !v)}
              icon={reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            >
              {reveal ? 'Hide passwords' : 'Reveal passwords'}
            </Button>
          }
        />

        {reveal ? (
          <div className="border-b border-surface-border p-4">
            <Alert severity="warning">
              App passwords are decrypted below. Every reveal is recorded in the audit log.
            </Alert>
          </div>
        ) : null}

        <div className="border-b border-surface-border p-4">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" aria-hidden />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search address or owner…"
              className="pl-9"
            />
          </div>
        </div>

        {isLoading ? (
          <TableSkeleton rows={10} columns={5} />
        ) : data?.items.length ? (
          <>
            <div className={clsx('overflow-x-auto transition-opacity', isPlaceholderData && 'opacity-60')}>
              <table className="table-base">
                <thead>
                  <tr>
                    <th>Email address</th>
                    <th>App password</th>
                    <th>Owner</th>
                    <th>Status</th>
                    <th className="text-right">Last check</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((mailbox) => (
                    <tr key={mailbox.id}>
                      <td>
                        <p className="font-mono text-xs text-slate-200">{mailbox.emailAddress}</p>
                        <p className="text-xs text-slate-600">{mailbox.label}</p>
                      </td>
                      <td>
                        {mailbox.appPassword ? (
                          <code className="break-all font-mono text-xs text-amber-300">{mailbox.appPassword}</code>
                        ) : (
                          <span className="font-mono text-xs text-slate-700">••••••••••••</span>
                        )}
                      </td>
                      <td>
                        <p className="text-xs text-slate-300">{mailbox.ownerName}</p>
                        <p className="truncate text-xs text-slate-600">{mailbox.ownerEmail}</p>
                      </td>
                      <td>
                        <Badge tone={statusTone(mailbox.syncStatus)}>{humanizeStatus(mailbox.syncStatus)}</Badge>
                      </td>
                      <td className="whitespace-nowrap text-right text-xs text-slate-500">
                        {formatRelative(mailbox.lastSyncAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination {...data} onPageChange={setPage} />
          </>
        ) : (
          <EmptyState
            icon={<Inbox className="h-6 w-6" />}
            title="No mailboxes yet"
            description="Mailboxes appear here as users connect them."
          />
        )}
      </Card>
    </div>
  );
}
